import React, { useState, useRef, useEffect } from 'react';
import { INSPIRATION_TAGS } from '../config/constants';
import { getTexts } from '../config/texts';
import { createVideoTask, getUserPoints, getVideoGenerationConfigs } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import './Generator.css';

const Generator = ({ language, setCurrentPage }) => {
  const { user, setShowLogin } = useAuth();
  const [storyText, setStoryText] = useState('');
  const [title, setTitle] = useState('');
  const [duration, setDuration] = useState(30);
  const [durationText, setDurationText] = useState('30s');
  const [showDurationList, setShowDurationList] = useState(false);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [pointsConfig, setPointsConfig] = useState([]);
  const [durations, setDurations] = useState([]); // v3.0: 从平台配置动态获取

  const fileInputRef = useRef(null);

  const texts = getTexts('generator', language);
  const tags = INSPIRATION_TAGS[language];

  // 加载积分配置
  useEffect(() => {
    const loadPointsConfig = async () => {
      try {
        const result = await getVideoGenerationConfigs();
        if (result.code === 200) {
          const configs = result.data?.configs || [];
          setPointsConfig(configs);

          // v3.0: 从配置动态生成时长选项
          // 支持 duration_seconds 字段或从 service_code 提取
          const durationOptions = configs
            .map(c => {
              // 优先使用 duration_seconds 字段
              let seconds = c.duration_seconds;
              if (!seconds) {
                // 如果没有 duration_seconds，尝试从 service_code 提取
                const match = c.service_code?.match(/(\d+)/);
                seconds = match ? parseInt(match[1]) : 0;
              }
              return {
                value: seconds,
                text: `${seconds}s`,
                points: c.points_required,
                service_code: c.service_code
              };
            })
            .filter(option => option.value > 0)  // 过滤掉无效的
            .sort((a, b) => a.value - b.value);

          if (durationOptions.length > 0) {
            setDurations(durationOptions);
            // 设置默认选中第一个
            setDuration(durationOptions[0].value);
            setDurationText(durationOptions[0].text);
          }
        }
      } catch (error) {
        console.error('Failed to load points config:', error);
        // 如果加载失败，使用默认配置
        setDurations([
          { value: 30, text: '30s', points: 50 },
          { value: 45, text: '45s', points: 70 },
          { value: 60, text: '60s', points: 90 }
        ]);
      }
    };

    loadPointsConfig();
  }, []);

  // 根据时长获取所需积分
  const getPointsRequired = (durationValue) => {
    const config = pointsConfig.find(c => {
      // 优先使用 duration_seconds 字段比较
      if (c.duration_seconds !== undefined) {
        return c.duration_seconds === durationValue;
      }
      // 兼容：从 service_code 提取秒数
      const match = c.service_code?.match(/(\d+)/);
      const seconds = match ? parseInt(match[1]) : 0;
      return seconds === durationValue;
    });
    return config?.points_required || 50;
  };

  const handleTagClick = (tag) => {
    setStoryText(texts.template(tag));
  };

  const handleDurationSelect = (value, text) => {
    setDuration(value);
    setDurationText(text);
    setShowDurationList(false);
  };

  const handleFileUpload = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // 验证文件类型
      if (!file.type.startsWith('image/')) {
        setError(texts.error);
        return;
      }

      // 验证文件大小（最大 10MB）
      if (file.size > 10 * 1024 * 1024) {
        setError(texts.fileTooLarge);
        return;
      }

      setUploadedImage(file);
      setError('');

      // 创建预览
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleGenerate = async () => {
    // 检查登录状态
    if (!user) {
      setShowLogin(true);
      return;
    }

    // 验证输入
    if (!uploadedImage) {
      setError(texts.selectImage);
      return;
    }

    if (!storyText.trim() && !title.trim()) {
      setError(texts.enterContent);
      return;
    }

    setGenerating(true);
    setError('');

    try {
      // 检查用户积分
      const pointsResult = await getUserPoints();
      if (pointsResult.code !== 200) {
        throw new Error(texts.cannotGetPoints);
      }

      const currentPoints = pointsResult.data?.available_points || 0;

      // 从接口获取的积分配置
      const pointsRequired = getPointsRequired(duration);

      if (currentPoints < pointsRequired) {
        if (typeof texts.insufficientPoints === 'function') {
          setError(texts.insufficientPoints(pointsRequired, currentPoints));
        } else {
          setError(`${texts.pointsRequired} ${pointsRequired} ${texts.points}, ${currentPoints} ${texts.points}`);
        }
        return;
      }

      // 准备表单数据
      const formData = new FormData();
      formData.append('user_id', user.id);  // 统一使用 user.id
      formData.append('user_src', 'web_global');
      formData.append('title', title || texts.unnamed);
      formData.append('topic', storyText || texts.myStory);
      formData.append('image', uploadedImage);
      formData.append('duration', duration);

      // 调用视频生成 API
      const result = await createVideoTask(formData);

      if (result.code === 200) {
        // 生成成功，跳转到我的作品页面
        alert(texts.success);
        if (setCurrentPage) {
          setCurrentPage('works');
        }
      } else {
        // 生成失败
        const errorMsg = result.message || texts.videoCreationFailed;
        if (result.code === 4004 || result.code === 4005) {
          setError(texts.rechargeRequired);
        } else {
          setError(errorMsg);
        }
      }
    } catch (err) {
      console.error('Generate video error:', err);
      setError(texts.networkError);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="gen-container">
      {error && (
        <div className="error-message">
          <i className="fas fa-exclamation-circle"></i>
          <span>{error}</span>
          <button onClick={() => setError('')} className="close-error">
            <i className="fas fa-times"></i>
          </button>
        </div>
      )}

      <div className="gen-layout">
        <div
          className={`upload-area ${imagePreview ? 'has-image' : ''}`}
          onClick={handleFileUpload}
        >
          {imagePreview ? (
            <img src={imagePreview} alt="Preview" className="image-preview" />
          ) : (
            <>
              <i className="fas fa-camera-retro"></i>
              <span>{texts.upload}</span>
            </>
          )}
          <input
            type="file"
            id="file-input"
            ref={fileInputRef}
            hidden
            accept="image/*"
            onChange={handleFileChange}
          />
        </div>

        <div className="input-area">
          <input
            type="text"
            placeholder={texts.titlePlaceholder}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="title-input"
          />

          <textarea
            placeholder={texts.placeholder}
            value={storyText}
            onChange={(e) => setStoryText(e.target.value)}
          />

          <div className="duration-dropdown">
            <div
              className="duration-trigger"
              onClick={(e) => {
                e.stopPropagation();
                setShowDurationList(!showDurationList);
              }}
            >
              <span id="selected-val">{durationText}</span>
              <i
                className="fas fa-chevron-up"
                style={{ transform: showDurationList ? 'rotate(180deg)' : 'rotate(0deg)' }}
              ></i>
            </div>
            <div className={`duration-list ${showDurationList ? 'show' : ''}`} id="duration-options">
              {durations.map((item) => (
                <div
                  key={item.value}
                  className={`duration-item ${duration === item.value ? 'active' : ''}`}
                  onClick={() => handleDurationSelect(item.value, item.text)}
                >
                  <span className="duration-text">{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          {user && (
            <div className="points-reminder">
              <i className="fas fa-info-circle"></i>
              <span>
                {texts.pointsRequired} {getPointsRequired(duration)} {texts.points}
              </span>
            </div>
          )}
        </div>

        <div className="btn-area">
          <button
            className="gen-btn"
            onClick={handleGenerate}
            disabled={generating}
          >
            {generating ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                <span>{texts.generating}</span>
              </>
            ) : (
              <>
                <i className="fas fa-magic"></i>
                <span>{texts.generate}</span>
              </>
            )}
          </button>
        </div>
      </div>

      <div className="tag-row">
        <span>{texts.inspiration}</span>
        {tags.map((tag, index) => (
          <div key={index} className="tag" onClick={() => handleTagClick(tag)}>
            {tag}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Generator;